const { saveRiotRecordSession } = require("./riotAccountSession");
const { isTransientNetworkError, readWithRetry, parseSdkResponse } = require("./riotRequestRecovery");
const { retryRiotAuthorization } = require("./riotRenewalPolicy");

const RIOT_AUTHORIZATION_URL = "https://auth.riotgames.com/authorize?redirect_uri=https%3A%2F%2Fplayvalorant.com%2Fopt_in&client_id=play-valorant-web-prod&response_type=token%20id_token&nonce=1&scope=account%20openid";
const RIOT_ENTITLEMENTS_URL = "https://entitlements.auth.riotgames.com/api/token/v1";
const RIOT_USERINFO_URL = "https://auth.riotgames.com/userinfo";
const RIOT_VSHOP_NOTIFICATION = "app.valorantRecord.renewSession";
const HIDDEN_WEBVIEW_KEYS = ["__valorant_record_manual_login_only__"];
const AUTHORIZATION_TIMEOUT_MS = 20000;
const TOKEN_TIMEOUT_MS = 10000;
const apiStates = new WeakMap();

function requiredText(value, errorCode) {
    if (typeof value !== "string" || !value.trim()) throw new Error(errorCode);
    return value.trim();
}

function parseAccessToken(uri) {
    const rawUri = requiredText(uri, "RIOT_TOKEN_INVALID");
    const hashIndex = rawUri.indexOf("#");
    const queryIndex = rawUri.indexOf("?");
    const paramsText = hashIndex >= 0
        ? rawUri.slice(hashIndex + 1)
        : (queryIndex >= 0 ? rawUri.slice(queryIndex + 1) : rawUri);
    return requiredText(new URLSearchParams(paramsText).get("access_token"), "RIOT_TOKEN_INVALID");
}

function responseState(uri) {
    if (typeof uri !== "string") return "";
    try {
        const url = new URL(uri);
        return new URLSearchParams(url.hash.slice(1)).get("state") || url.searchParams.get("state") || "";
    } catch (error) {
        return "";
    }
}

function createAttemptState() {
    const random = typeof crypto !== "undefined" ? crypto : null;
    if (!random || typeof random.getRandomValues !== "function") {
        throw new Error("RIOT_VSHOP_STATE_UNAVAILABLE");
    }
    return Array.from(random.getRandomValues(new Uint32Array(4)),
        (value) => value.toString(16).padStart(8, "0")).join("");
}

function getRiotVShopBridgeScriptUrl(pageHref) {
    return new URL("riotRecordSessionBridge.js", requiredText(pageHref, "RIOT_VSHOP_PAGE_URL_MISSING")).href;
}

function parseNotificationPayload(rawPayload) {
    let text = typeof rawPayload === "string" ? rawPayload : "";
    try {
        text = decodeURIComponent(text);
    } catch (error) {
        throw new Error("RIOT_VSHOP_NOTIFICATION_INVALID");
    }
    try {
        const payload = JSON.parse(text);
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) throw new Error("RIOT_VSHOP_NOTIFICATION_INVALID");
        return payload;
    } catch (error) {
        if (text.includes("access_token=")) return { status: "success", url: text };
        throw new Error("RIOT_VSHOP_NOTIFICATION_INVALID");
    }
}

function getApiState(appApi, notificationName) {
    let state = apiStates.get(appApi);
    if (!state) {
        state = { pending: null, active: false, listeners: new Set() };
        apiStates.set(appApi, state);
    }
    if (state.listeners.has(notificationName)) return state;
    appApi.UI.registerNotificationListener(notificationName, (rawPayload) => {
        if (!state.pending || state.pending.notificationName !== notificationName) return;
        let payload;
        try {
            payload = parseNotificationPayload(rawPayload);
        } catch (error) {
            // An uncorrelated or late notification must not fail the current attempt.
            return;
        }
        if (responseState(payload.url) !== state.pending.attemptState) return;
        if (payload.status === "success") {
            state.pending.finish(null, payload.url);
            return;
        }
        if (payload.status === "login_required" || payload.status === "interaction_required") {
            state.pending.finish(new Error("RIOT_VSHOP_LOGIN_REQUIRED"));
        }
    });
    state.listeners.add(notificationName);
    return state;
}

function runStage(attempt, stage, timeoutMs, timeoutCode, invoke) {
    return new Promise((resolve, reject) => {
        const timeoutError = () => Object.assign(new Error(timeoutCode), { code: timeoutCode, transientNetwork: true });
        const startedAt = Date.now();
        let settled = false;
        let cleanup;
        const finish = (error, value) => {
            if (settled) return;
            try { attempt.assertCurrent(); } catch (obsolete) { error = obsolete; }
            if (!error && Date.now() - startedAt >= timeoutMs) error = timeoutError();
            settled = true;
            clearTimeout(timer);
            clearInterval(currentTimer);
            attempt.cancellations.delete(cancel);
            if (cleanup) cleanup();
            // Never log the authorization URL, notification, account or response body.
            try {
                console.info("[Riot renewal stage]", {
                    stage, success: !error, elapsedMs: Date.now() - startedAt,
                    errorCode: error ? (/^RIOT_[A-Z0-9_]+$/.test(error.message) ? error.message : "RIOT_VSHOP_RENEW_UNAVAILABLE") : "",
                });
            } catch (loggingError) { /* Observability must not affect authentication. */ }
            if (error) reject(error);
            else resolve(value);
        };
        const cancel = () => finish(new Error("RIOT_VSHOP_STAGE_CANCELLED"));
        const timer = setTimeout(() => finish(timeoutError()), timeoutMs);
        const currentTimer = setInterval(() => {
            try { attempt.assertCurrent(); } catch (error) { finish(error); }
        }, 100);
        attempt.cancellations.add(cancel);
        try {
            attempt.assertCurrent();
            cleanup = invoke(finish);
            if (settled && cleanup) cleanup();
        } catch (error) {
            finish(error);
        }
    });
}

function waitForNativeAccessToken(appApi, state, attempt, scriptUrl, timeoutMs, options) {
    return runStage(attempt, "AUTHORIZATION_CALLBACK", timeoutMs, "RIOT_VSHOP_RENEW_TIMEOUT", (finish) => {
        const pending = { finish, attemptState: attempt.id, notificationName: options.notificationName };
        state.pending = pending;
        try {
            appApi.UI.openUrlIn2thWebView(
                `${RIOT_AUTHORIZATION_URL}&state=${encodeURIComponent(attempt.id)}`,
                scriptUrl,
                options.visibilityKeys || HIDDEN_WEBVIEW_KEYS
            );
        } catch (error) {
            finish(new Error("RIOT_VSHOP_RENEW_UNAVAILABLE"));
        }
        return () => { if (state.pending === pending) state.pending = null; };
    });
}

function sendTokenRequest(appApi, attempt, stage, url, accessToken, timeoutMs) {
    const deadline = Date.now() + timeoutMs * 2;
    return readWithRetry(() => runStage(attempt, stage, Math.max(1, Math.min(timeoutMs, deadline - Date.now())), `RIOT_VSHOP_${stage}_TIMEOUT`, (finish) => {
        try {
            const headers = { Authorization: `Bearer ${decodeURIComponent(accessToken)}` };
            const dispatched = appApi.Net.send(url, "POST", "application/json", headers, {}, (response, responseHeaders) => {
                try {
                    response = parseSdkResponse(url, headers, response, responseHeaders).data;
                    if (stage === "ENTITLEMENTS") {
                        requiredText(response && response.entitlements_token, "RIOT_ENTITLEMENTS_INVALID");
                    } else {
                        const puuid = requiredText(response && response.sub, "RIOT_USERINFO_INVALID");
                        if (puuid !== attempt.puuid) throw new Error("RIOT_ACCOUNT_MISMATCH");
                    }
                    finish(null, response);
                } catch (error) {
                    finish(error);
                }
            });
            if (dispatched === false) finish(new Error("RIOT_VSHOP_TOKEN_REQUEST_FAILED"));
        } catch (error) {
            finish(Object.assign(new Error("RIOT_VSHOP_TOKEN_REQUEST_FAILED"), { transientNetwork: isTransientNetworkError(error) }));
        }
    }), { check: () => attempt.assertCurrent(), now: () => Date.now(), deadline, retryDelayMs: 0 });
}

async function requestRiotSessionWithVShopWebView(appApi, expectedPuuid, options = {}) {
    const puuid = requiredText(expectedPuuid, "RIOT_SESSION_NOT_READY");
    if (!appApi || !appApi.UI
        || typeof appApi.UI.registerNotificationListener !== "function"
        || typeof appApi.UI.openUrlIn2thWebView !== "function"
        || !appApi.Net || typeof appApi.Net.send !== "function") {
        throw new Error("RIOT_VSHOP_RENEW_UNAVAILABLE");
    }
    const scriptUrl = requiredText(options.scriptUrl, "RIOT_VSHOP_SCRIPT_URL_MISSING");
    const stageTimeout = (value, fallback) => Number.isFinite(Number(value)) && Number(value) > 0
        ? Math.min(Number(value), fallback) : fallback;
    const authorizationTimeout = stageTimeout(options.authorizationTimeoutMs || options.timeoutMs, AUTHORIZATION_TIMEOUT_MS);
    const tokenTimeout = stageTimeout(options.tokenTimeoutMs || options.timeoutMs, TOKEN_TIMEOUT_MS);
    const notificationName = options.notificationName || RIOT_VSHOP_NOTIFICATION;
    const state = getApiState(appApi, notificationName);
    if (state.active) throw new Error("RIOT_VSHOP_RENEWAL_BUSY");
    const attempt = {
        id: createAttemptState(),
        puuid,
        cancellations: new Set(),
        finished: false,
        assertCurrent() {
            if (options.isCurrent && !options.isCurrent()) throw new Error("RIOT_LOGIN_OBSOLETE");
            if (this.finished) throw new Error("RIOT_VSHOP_STAGE_CANCELLED");
        },
    };
    attempt.assertCurrent();
    state.active = true;
    try {
        const tokenUri = await retryRiotAuthorization(() => {
            attempt.id = createAttemptState();
            return waitForNativeAccessToken(appApi, state, attempt, scriptUrl, authorizationTimeout,
                { ...options, notificationName });
        }, () => attempt.assertCurrent());
        attempt.assertCurrent();
        const accessToken = parseAccessToken(tokenUri);
        const [entitlements, userinfo] = await Promise.all([
            sendTokenRequest(appApi, attempt, "ENTITLEMENTS", RIOT_ENTITLEMENTS_URL, accessToken, tokenTimeout),
            sendTokenRequest(appApi, attempt, "USERINFO", RIOT_USERINFO_URL, accessToken, tokenTimeout),
        ]);
        attempt.assertCurrent();
        const entitlementsToken = requiredText(
            entitlements && entitlements.entitlements_token,
            "RIOT_ENTITLEMENTS_INVALID"
        );
        const returnedPuuid = requiredText(userinfo && userinfo.sub, "RIOT_USERINFO_INVALID");
        if (returnedPuuid !== puuid) throw new Error("RIOT_ACCOUNT_MISMATCH");

        return { puuid, accessToken, entitlementsToken, loginMode: "VShopMode" };
    } finally {
        attempt.finished = true;
        for (const cancel of Array.from(attempt.cancellations)) cancel();
        state.pending = null;
        state.active = false;
    }
}

async function renewRiotSessionWithVShopWebView(appApi, storage, expectedPuuid, options = {}) {
    const session = await requestRiotSessionWithVShopWebView(appApi, expectedPuuid, options);
    if (options.isCurrent && !options.isCurrent()) throw new Error("RIOT_LOGIN_OBSOLETE");
    saveRiotRecordSession(storage, session);
    return session;
}

module.exports = {
    RIOT_VSHOP_NOTIFICATION,
    getRiotVShopBridgeScriptUrl,
    parseAccessToken,
    requestRiotSessionWithVShopWebView,
    renewRiotSessionWithVShopWebView,
};
